import { useState, useMemo } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import Footer from "../components/Footer";

const mediaItems = [
  {
    id: 1,
    type: "video",
    category: "City Council",
    title: "City Council Briefing: Small Business Insurance Access Bill",
    date: "2026-06-10",
    duration: "14:20 Mins",
    location: "Chamber Hall, District 4",
    embedUrl: "https://www.youtube.com/embed/VIDEO_ID_HERE",
    description:
      "A formal public floor address advocating for structural policy amendments. The discussion outlines pathways to mitigate localized premium spikes and preserve grassroots commercial spaces amid zoning adjustments.",
    tags: ["Legislative Policy", "Commercial Access", "Public Hearing"]
  },
  {
    id: 2,
    type: "audio",
    category: "Radio",
    title: "Morning Business Hour: Mitigating Operational Risk in Volatile Markets",
    date: "2026-05-22",
    duration: "28:45 Mins",
    location: "WBRC Metro Broadcast Studio",
    audioUrl: "/media/audio/morning-business-hour-05-22.mp3",
    description:
      "An analytical panel broadcast exploring systemic blind spots in enterprise-level property coverage. Key segments cover inflation-adjusted valuation tracking and proactive asset hedging for mid-sized operations.",
    tags: ["Risk Underwriting", "Market Liquidity", "Radio Feature"]
  },
  {
    id: 3,
    type: "video",
    category: "Community",
    title: "Town Hall Forum: Protecting Local Venues & Arts Infrastructure",
    date: "2026-04-15",
    duration: "42:10 Mins",
    location: "Community Center Theater",
    embedUrl: "https://www.youtube.com/embed/VIDEO_ID_HERE",
    description:
      "A collaborative town hall discussion addressing the financial liabilities, event-specific insurance mandates, and structural compliance regulations impacting regional performance artists and venue operators.",
    tags: ["Community Arts", "Liability Frameworks", "Town Hall"]
  },
  {
    id: 4,
    type: "audio",
    category: "Radio",
    title: "Enterprise Spotlight: Navigating Multi-State Carrier Workflows",
    date: "2026-03-08",
    duration: "18:15 Mins",
    location: "Syndicated Business Audio Network",
    audioUrl: "/media/audio/enterprise-spotlight.mp3",
    description:
      "An expert breakdown outlining policy placement strategies across shifting regional frameworks. Focuses on document auditing workflows and streamlining structural multi-page carrier forms.",
    tags: ["Carrier Audits", "Asset Structuring", "Compliance"]
  }
];

const categories = ["All", "City Council", "Radio", "Community"];

// The flagship podcast currently has a single pilot episode, surfaced in the Podcast Spotlight section below.
const podcastEpisodes = [
  {
    id: "ep-01",
    number: "01 — Pilot",
    title: "What Is Insurance?",
    duration: "—",
    date: null,
    description:
      "The premise for the whole show: what insurance actually is, why everyone in America needs it, and what this podcast will unpack episode by episode."
  }
];

const podcastPlatforms = [
  {
    name: "Apple Podcasts",
    href: "#",
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 4.8a4.365 4.365 0 0 1 4.365 4.365c0 1.44-.72 2.7-1.815 3.465v.045c1.44.63 2.43 2.07 2.43 3.735 0 2.25-1.82 4.095-4.095 4.095h-1.77c-2.25 0-4.095-1.845-4.095-4.095 0-1.665.99-3.105 2.43-3.735v-.045A4.36 4.36 0 0 1 7.635 9.165 4.365 4.365 0 0 1 12 4.8z"/>
      </svg>
    )
  },
  {
    name: "Spotify",
    href: "#",
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34a.748.748 0 0 1-1.031.245c-2.822-1.723-6.376-2.112-10.56-1.158a.746.746 0 0 1-.331-1.454c4.578-1.047 8.502-.596 11.677 1.336a.747.747 0 0 1 .245 1.031zm1.464-3.264a.935.935 0 0 1-1.288.307c-3.23-1.985-8.153-2.561-11.975-1.401a.936.936 0 1 1-.543-1.79c4.368-1.326 9.798-.684 13.5 1.596a.935.935 0 0 1 .306 1.288zm.126-3.402c-3.874-2.301-10.263-2.513-13.963-1.39a1.123 1.123 0 1 1-.652-2.148c4.246-1.289 11.302-1.04 15.757 1.605a1.124 1.124 0 0 1-1.142 1.933z"/>
      </svg>
    )
  },
  {
    name: "YouTube",
    href: "#",
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    )
  },
  {
    name: "RSS Feed",
    href: "#",
    icon: (
      <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
        <path d="M6.503 20.752a2.251 2.251 0 1 1-4.502 0 2.251 2.251 0 0 1 4.502 0zM1.001 8.339v4.517c6.13.359 11.048 5.276 11.404 11.404h4.517C16.545 15.406 9.756 8.617 1.001 8.339zm0-7.339v4.518c11.475.348 20.663 9.532 21.001 21.002h4.518C26.208 12.108 14.955.848 1.001.528V1z"/>
      </svg>
    )
  }
];

function PodcastSpotlight() {
  return (
    <section className="w-full bg-white-warm py-20 px-6 sm:px-8 md:px-16 border-b border-blue-dark/5">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">

          {/* Signature dial — an on-air ring built from the same waveform language used on the audio cards above */}
          <div className="lg:col-span-4 flex justify-center lg:justify-start">
            <div className="relative w-52 h-52 sm:w-64 sm:h-64 shrink-0">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-dark to-blue-primary shadow-[0_20px_50px_rgba(1,46,114,0.25)]" />
              <div className="absolute inset-[10px] rounded-full border border-white-pure/20" />
              <div className="absolute inset-[22px] rounded-full border border-white-pure/10 flex items-center justify-center overflow-hidden">
                <div className="flex items-end gap-1.5 h-14">
                  {[5, 9, 6, 12, 8, 4, 10, 7, 5].map((val, idx) => (
                    <div
                      key={idx}
                      className="w-1.5 rounded-full bg-white-pure/80"
                      style={{ height: `${val * 8}%` }}
                    />
                  ))}
                </div>
              </div>
              <span className="absolute -bottom-1 -right-1 bg-white-pure text-blue-dark font-cinzel text-[9px] tracking-widest uppercase px-3 py-1.5 rounded-full shadow-md border border-blue-dark/10">
                On Air
              </span>
            </div>
          </div>

          {/* Copy + platform links */}
          <div className="lg:col-span-8">
            <span className="font-cinzel text-[11px] tracking-widest uppercase text-blue-primary font-bold block mb-4">
              Flagship Podcast
            </span>
            <h3 className="font-cinzel text-3xl sm:text-4xl md:text-5xl font-bold text-blue-dark leading-tight tracking-tight mb-4">
              Everybody in America{" "}
              <span className="font-constantia italic font-normal block sm:inline">
                Needs Insurance
              </span>
            </h3>
            <p className="font-times text-black-deep/60 text-sm mb-6">
              Hosted by <span className="text-blue-dark font-semibold">Denise Pomboonreung</span>
            </p>

            <p className="font-times text-black-deep/75 text-base leading-relaxed mb-8 max-w-2xl">
              A weekly conversation on the policies, loopholes, and everyday decisions that
              determine who gets covered, and who's left exposed. New episodes break down
              real cases, plain-language explainers, and the questions worth asking before
              you sign anything.
            </p>

            {/* Platform links */}
            <div className="flex flex-wrap gap-3 mb-10">
              {podcastPlatforms.map((platform) => (
                <a
                  key={platform.name}
                  href={platform.href}
                  className="flex items-center gap-2 font-cinzel text-[10px] tracking-widest uppercase text-blue-dark bg-white-pure border border-blue-dark/10 px-4 py-2.5 rounded-sm hover:border-blue-primary hover:text-blue-primary transition-colors shadow-sm"
                >
                  {platform.icon}
                  {platform.name}
                </a>
              ))}
            </div>

            {/* Episode(s) */}
            <div className="border-t border-blue-dark/10 pt-6">
              <h4 className="font-cinzel text-xs tracking-widest text-blue-dark/50 uppercase font-bold mb-5">
                {podcastEpisodes.length > 1 ? "Latest Episodes" : "Now Streaming"}
              </h4>
              <div className="flex flex-col divide-y divide-blue-dark/5">
                {podcastEpisodes.map((ep) => (
                  <div key={ep.id} className="flex items-start justify-between gap-6 py-4 group">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 mb-1.5">
                        <span className="font-cinzel text-[10px] tracking-widest uppercase text-blue-primary font-bold">
                          Episode {ep.number}
                        </span>
                        {ep.date && (
                          <>
                            <span className="text-black-deep/30 text-xs">•</span>
                            <span className="font-mono text-[11px] text-black-deep/40">
                              {new Date(ep.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            </span>
                          </>
                        )}
                      </div>
                      <h5 className="font-constantia text-blue-dark font-bold text-lg mb-1 group-hover:text-blue-primary transition-colors cursor-pointer">
                        {ep.title}
                      </h5>
                      <p className="font-times text-black-deep/60 text-sm leading-relaxed max-w-xl">
                        {ep.description}
                      </p>
                    </div>
                    {ep.duration !== "—" && (
                      <span className="shrink-0 font-mono text-xs text-black-deep/40 bg-black-deep/5 px-2.5 py-1 rounded-full">
                        {ep.duration}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MediaCard({ item, isFeatured = false }) {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div 
      className={`bg-white-pure rounded-sm flex flex-col overflow-hidden border border-blue-dark/5 bg-gradient-to-b from-white-pure to-white-warm/30 shadow-[0_4px_24px_rgba(1,46,114,0.03)] hover:shadow-[0_16px_40px_rgba(1,46,114,0.08)] transition-all duration-500 transform hover:-translate-y-0.5 ${
        isFeatured ? "lg:grid lg:grid-cols-12 lg:gap-2" : ""
      }`}
    >
      {/* Media Source Preview Area */}
      <div className={`${isFeatured ? "lg:col-span-7" : "w-full"} relative group/media`}>
        {item.type === "video" && (
          <div className="w-full relative aspect-video bg-blue-dark/90">
            <iframe
              src={item.embedUrl}
              title={item.title}
              className="absolute top-0 left-0 w-full h-full opacity-95 transition-opacity group-hover/media:opacity-100"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}

        {item.type === "audio" && (
          <div className="w-full aspect-video bg-gradient-to-br from-blue-dark via-blue-dark to-blue-primary flex flex-col items-center justify-center p-8 text-center select-none relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(1,46,114,0.4),transparent)] mix-blend-overlay" />
            
            {/* Audio Branding Matrix */}
            <span className="font-cinzel text-[10px] tracking-widest text-white-pure/40 uppercase mb-3 relative z-10">
              Broadcast Audio Archive
            </span>

            {/* Simulated Animated Waveform Module */}
            <div className="flex items-end justify-center gap-1.5 h-16 mb-4 relative z-10 px-4 w-full max-w-xs">
              {[3, 6, 8, 5, 9, 11, 6, 4, 8, 12, 7, 5, 9, 4, 7, 10, 5, 3].map((val, idx) => (
                <div 
                  key={idx} 
                  className={`w-1 rounded-full bg-blue-primary transition-all duration-300 ${
                    isPlaying ? "animate-pulse" : "opacity-40"
                  }`} 
                  style={{ 
                    height: isPlaying ? `${val * 8}%` : "15%",
                    animationDelay: `${idx * 75}ms` 
                  }} 
                />
              ))}
            </div>

            <button 
              onClick={() => setIsPlaying(!isPlaying)}
              className="w-12 h-12 rounded-full bg-white-pure text-blue-dark flex items-center justify-center shadow-lg hover:scale-105 transition-all relative z-10 active:scale-95"
            >
              {isPlaying ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
              ) : (
                <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Main Copy Context Section */}
      <div className={`flex flex-col flex-1 p-6 md:p-8 lg:p-9 justify-between ${isFeatured ? "lg:col-span-5" : ""}`}>
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <span className="font-cinzel text-[11px] tracking-widest uppercase text-blue-primary font-bold">
              {item.category}
            </span>
            <div className="flex items-center gap-2 text-black-deep/40 text-xs font-mono bg-black-deep/5 px-2.5 py-1 rounded-full">
              <span>{item.duration}</span>
              <span>•</span>
              <span className="truncate max-w-[140px]">{item.location}</span>
            </div>
          </div>

          <h3 className={`font-constantia text-blue-dark font-bold leading-tight mb-3 tracking-tight hover:text-blue-primary transition-colors cursor-pointer ${
            isFeatured ? "text-2xl md:text-3xl lg:text-3xl" : "text-xl md:text-2xl"
          }`}>
            {item.title}
          </h3>

          <p className="font-times text-black-deep/50 text-xs mb-4">
            {new Date(item.date).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </p>
          
          <div className="w-14 h-px bg-blue-primary/30 mb-5" />

          {/* Render standard core platform controller fallback */}
          {item.type === "audio" && (
            <audio 
              onPlay={() => setIsPlaying(true)} 
              onPause={() => setIsPlaying(false)} 
              src={item.audioUrl} 
              className="sr-only" 
              controls
            />
          )}

          <p className="font-times text-black-deep/75 text-sm leading-relaxed mb-6">
            {item.description}
          </p>
        </div>

        <div>
          {/* Metadata Categorization Pill Array */}
          <div className="flex flex-wrap gap-1.5 mb-5">
            {item.tags.map(tag => (
              <span key={tag} className="text-[10px] uppercase tracking-wider font-cinzel bg-white-warm border border-blue-dark/5 text-black-deep/60 px-2 py-0.5 rounded-sm">
                #{tag}
              </span>
            ))}
          </div>

          <div className="pt-4 border-t border-blue-dark/5 flex justify-end">
            <a href="#press-inquiries" className="font-cinzel text-[10px] tracking-widest uppercase text-blue-dark/60 hover:text-blue-primary transition-colors flex items-center gap-2 group">
              Request Public Records Transcript 
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3 h-3 transform group-hover:translate-x-1 transition-transform">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MediaPage() {
  usePageTitle("Media Archive & Press Room");
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredItems = useMemo(() => {
    return mediaItems.filter((item) => {
      const matchesCategory = activeCategory === "All" || item.category === activeCategory;
      const matchesSearch = 
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const featuredItem = mediaItems[0];
  const displayItems = filteredItems.filter(item => !(activeCategory === "All" && searchQuery === "" && item.id === featuredItem.id));

  return (
    <div className="w-full bg-white-pure">
      <main>
        {/* HERO HEADER SECTION */}
        <section className="w-full bg-gradient-to-b from-white-warm to-white-pure py-20 md:py-28 px-6 sm:px-8 md:px-16 border-b border-blue-dark/5">
          <div className="max-w-6xl mx-auto">
            
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-8 bg-blue-dark/40" />
              <span className="font-cinzel text-xs tracking-widest uppercase text-blue-dark/70">
                Archival Records Room
              </span>
            </div>

            <h2 className="font-cinzel text-4xl sm:text-5xl md:text-6xl font-bold text-blue-dark leading-none tracking-tight mb-6">
              Media &amp;{" "}
              <span className="font-constantia italic font-normal block sm:inline text-blue-dark">
                Public Appearances
              </span>
            </h2> 

            <p className="font-times text-black-deep/70 text-base md:text-lg leading-relaxed max-w-2xl">
              An indexed repository of legislative statements, enterprise-level risk commentaries, town hall updates, and community advocacy features.
            </p>
          </div>
        </section>

        {/* INTERACTIVE MEDIA GRID & MATRIX SECTION */}
        <section className="w-full py-16 px-6 sm:px-8 md:px-16">
          <div className="max-w-6xl mx-auto">
            
            {/* Control Panel System */}
            <div className="flex flex-col lg:flex-row gap-6 justify-between items-start lg:items-center mb-12 border-b border-blue-dark/10 pb-6">
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => {
                  const isActive = activeCategory === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => {
                        setActiveCategory(cat);
                        setSearchQuery("");
                      }}
                      className={`font-cinzel text-[11px] tracking-widest uppercase px-5 py-2.5 rounded-sm transition-all duration-300 border ${
                        isActive
                          ? "bg-blue-dark text-white-pure border-blue-dark shadow-md"
                          : "border-transparent text-blue-dark/60 hover:text-blue-dark hover:bg-blue-dark/5"
                      }`}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>

              {/* Dynamic Search Interface */}
              <div className="w-full lg:w-80 relative">
                <input 
                  type="text"
                  placeholder="Search titles, transcripts, or tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white-pure border border-blue-dark/10 rounded-sm py-3 pl-4 pr-10 text-xs font-times text-black-deep placeholder-black-deep/40 focus:outline-none focus:border-blue-primary transition-colors shadow-inner"
                />
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="w-4 h-4 absolute right-3 top-3.5 text-blue-dark/40">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.603 10.601Z" />
                </svg>
              </div>
            </div>

            {/* Pagination / Database Index Counter */}
            <div className="flex items-center justify-between text-xs font-mono text-black-deep/40 mb-8 bg-white-warm/50 p-3 rounded-sm border border-blue-dark/5">
              <span>System Index Ledger: Verification Active</span>
              <span>Showing {filteredItems.length} of {mediaItems.length} Documents</span>
            </div>

            {/* Central Stream Layout Engine */}
            {filteredItems.length === 0 ? (
              <div className="py-24 border border-dashed border-blue-dark/10 text-center rounded-sm bg-white-warm/30">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="w-10 h-10 mx-auto mb-3 text-blue-dark/30">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.603 10.601Z" />
                </svg>
                <p className="font-times text-black-deep/50 text-base italic">
                  No documented statements match the designated structural search parameters.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-12">
                
                {/* Centerpiece Hero Layout Card */}
                {activeCategory === "All" && searchQuery === "" && (
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-2 h-2 rounded-full bg-blue-primary animate-ping" />
                      <span className="font-cinzel text-[10px] tracking-widest text-blue-dark/60 uppercase font-bold">
                        Featured Engagement Segment
                      </span>
                    </div>
                    <MediaCard item={featuredItem} isFeatured={true} />
                  </div>
                )}

                {/* Secondary Feed Framework */}
                {displayItems.length > 0 && (
                  <div>
                    {activeCategory === "All" && searchQuery === "" && (
                      <h4 className="font-cinzel text-xs tracking-widest text-blue-dark/40 uppercase font-bold mb-6 border-b border-blue-dark/5 pb-2">
                        Historical Archives
                      </h4>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
                      {displayItems.map((item) => (
                        <MediaCard key={item.id} item={item} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* PODCAST SPOTLIGHT SECTION */}
        <PodcastSpotlight />

        {/* SIGNIFICANT NEW SECTION: PRESS KIT & MEDIA REQUEST INTERFACE */}
        <section id="press-inquiries" className="w-full bg-gradient-to-br from-blue-dark to-blue-primary text-white-pure py-20 px-6 sm:px-8 md:px-16 relative overflow-hidden shadow-inner">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(255,255,255,0.06),transparent)] pointer-events-none" />
          
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              
              {/* Informational Callout Column */}
              <div className="lg:col-span-5">
                <span className="font-cinzel text-[11px] tracking-widest uppercase text-white-pure/60 block mb-4">
                  Press Room Services
                </span>
                <h3 className="font-cinzel text-3xl md:text-4xl font-bold leading-tight mb-4">
                  Booking &amp; Media <br />
                  <span className="font-constantia italic font-normal text-white-pure/85">Resource Access</span>
                </h3>
                <p className="font-times text-white-pure/75 text-sm leading-relaxed mb-8">
                  For formal schedule requests, platform keynotes, policy commentary panels, or to obtain verified high-resolution professional headshots and commercial biography documentation, access the ledger links or route an alert via administrative channels.
                </p>

                {/* Resource Link Grouping */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="/docs/press-kit-2026.pdf" download className="bg-white-pure text-blue-dark font-cinzel text-[11px] tracking-widest uppercase px-6 py-3 rounded-sm font-bold hover:bg-white-warm transition-all text-center shadow-md">
                    Download Press Kit (PDF)
                  </a>
                  <a href="/images/assets-highres.zip" download className="border border-white-pure/30 text-white-pure font-cinzel text-[11px] tracking-widest uppercase px-6 py-3 rounded-sm hover:bg-white-pure/10 transition-all text-center">
                    Media Assets (.ZIP)
                  </a>
                </div>
              </div>

              {/* Functional Quick Alert / Booking Inflow Form */}
              <div className="lg:col-span-7 bg-white-pure/5 backdrop-blur-md p-8 rounded-sm border border-white-pure/10 shadow-xl">
                <h4 className="font-cinzel text-xs tracking-widest uppercase text-white-pure/90 mb-6 font-bold">
                  Direct Engagement Inquiry Form
                </h4>
                
                <form onSubmit={(e) => e.preventDefault()} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="font-cinzel text-[9px] tracking-widest uppercase text-white-pure/60">Full Name</label>
                    <input type="text" required className="bg-white-pure/10 border border-white-pure/10 rounded-sm p-2.5 text-xs focus:outline-none focus:border-white-pure text-white-pure font-times" />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="font-cinzel text-[9px] tracking-widest uppercase text-white-pure/60">Organization / Agency</label>
                    <input type="text" required className="bg-white-pure/10 border border-white-pure/10 rounded-sm p-2.5 text-xs focus:outline-none focus:border-white-pure text-white-pure font-times" />
                  </div>
                  <div className="flex flex-col sm:col-span-2 gap-1.5">
                    <label className="font-cinzel text-[9px] tracking-widest uppercase text-white-pure/60">Email Address</label>
                    <input type="email" required className="bg-white-pure/10 border border-white-pure/10 rounded-sm p-2.5 text-xs focus:outline-none focus:border-white-pure text-white-pure font-times" />
                  </div>
                  <div className="flex flex-col sm:col-span-2 gap-1.5">
                    <label className="font-cinzel text-[9px] tracking-widest uppercase text-white-pure/60">Engagement Brief Description</label>
                    <textarea rows={3} required className="bg-white-pure/10 border border-white-pure/10 rounded-sm p-2.5 text-xs focus:outline-none focus:border-white-pure text-white-pure font-times resize-none" placeholder="Specify project scope, tracking dates, and logistical mandates..." />
                  </div>
                  
                  <div className="sm:col-span-2 pt-2">
                    <button type="submit" className="w-full bg-white-pure text-blue-dark font-cinzel text-[11px] tracking-widest uppercase py-3 font-bold rounded-sm hover:bg-white-warm transition-colors shadow-md">
                      Transmit Administrative Request
                    </button>
                  </div>
                </form>

              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
